import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Search } from "lucide-react";
import { searchProducts } from "@/services/api";
import { CatalogTable } from "@/components/CatalogTable";
import { ProductDetailPanel } from "@/components/ProductDetailPanel";
import { NewProductModal } from "@/components/NewProductModal";
import { ImportCsvModal } from "@/components/ImportCsvModal";
import type { CatalogProduct } from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke } from "@/styles/tokens";

// ⚠️ THE CATEGORY OPTIONS ARE THE CLOSED UNION; BRAND AND ORIGIN ARE NOT.
//
// The asymmetry is deliberate and it is worth understanding before "fixing"
// it. ProductCategory is a three-value union the rest of this app compares
// with `===` (brand-lock evaluation keys on it), so restating it here matches
// what the code can actually handle. Brand and origin are restated NOWHERE
// any more: a hardcoded brand list silently made the one real product
// unfilterable the moment Odoo classified it as "Poly", and a hardcoded
// origin list offered four mock warehouses that match nothing on the real
// master. Both are now derived from the rows actually on screen -- see
// brandOptions / originOptions below.
const ALL_CATEGORIES = ["All", "Copper", "Fiber", "Accessory"];

// ⚠️ A BROWSE SCREEN ASKS FOR MORE THAN A SEARCH BOX DOES. The endpoint
// defaults to 50 and caps at 500 (_DEFAULT_LIST_LIMIT / _MAX_LIST_LIMIT in
// controllers/main.py). 200 is a page a person can scroll; anything the limit
// truncates is SAID so, under the count, because a silently-clipped list
// reads as the whole catalog.
const CATALOG_LIMIT = 200;

const SELECT_STYLE: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, fontSize: typography.fontSize.base,
  color: semanticColors.textPrimary, backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none",
};

export default function CatalogPage() {
  // ---- The real product master -------------------------------------------
  //
  // ⚠️ NO MOCK FALLBACK ANYWHERE ON THIS PATH. On any failure the rows are
  // CLEARED and an error is shown. Falling back to mocks/products.ts would
  // put 412 invented parts behind a screen that looks like it is showing the
  // real catalog -- and someone would go on to quote one. Two catalogs live
  // at once behind one control is the same class of bug the two-keys-live
  // cutover existed to kill, and the picker already refuses it (slice 1b).
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [catalogStatus, setCatalogStatus] = useState<"loading" | "ok" | "error">("loading");
  const [catalogError, setCatalogError] = useState("");
  // Bumped by Retry. A separate counter rather than re-setting `search` to its
  // current value: React bails out of a state update that is Object.is-equal,
  // so setSearch(q => q) would not re-run the fetch effect at all and the
  // button would silently do nothing.
  const [retryTick, setRetryTick] = useState(0);

  // global-search: /catalog?q=PART opens with the box already filled (Layout
  // remounts this page when the search string changes). The server still does
  // the searching; this only seeds the query.
  const [search, setSearch] = useState(() => {
    try { return (new URLSearchParams(window.location.search).get("q") ?? "").trim().slice(0, 80); }
    catch { return ""; }
  });
  // The query the CURRENT rows were fetched for, so the no-match message can
  // quote what was actually searched rather than what is in the box this
  // instant (they differ while a keystroke is still debouncing).
  const [searchedFor, setSearchedFor] = useState("");
  const [brand, setBrand] = useState<string>("All");
  const [category, setCategory] = useState<string>("All");
  const [origin, setOrigin] = useState<string>("All");
  const [selected, setSelected] = useState<CatalogProduct | null>(null);

  // ---- Add Product (slice 1d-b) --------------------------------------------
  //
  // The one write path from this screen into the master.
  const [addOpen, setAddOpen] = useState(false);
  // Bulk create from a CSV (slice import-b). Separate state from addOpen:
  // they are two different acts and only one may be open at a time.
  const [importOpen, setImportOpen] = useState(false);
  // The id of a just-created product to select once the server hands it back.
  //
  // ⚠️ NOT AN OPTIMISTIC ROW. Nothing is prepended to `products`: letting the
  // record come back from the server is what PROVES it persisted, the same
  // rule the save path follows in treating a missing quote_id as a failure. A
  // locally-inserted row would look identical to a saved one and would vanish
  // on the next refresh with no explanation.
  //
  // ⚠️ A REF, NOT STATE, and for the same reason fetchToken below is one: the
  // load effect READS it, so as state it would have to be a dependency -- and
  // adding it there re-fires the whole debounced fetch every time it changes,
  // including the moment the effect itself clears it. Nothing renders from it,
  // so there is no re-render to want.
  const pendingSelectIdRef = useRef<number | null>(null);
  // Said only when a create succeeded and the refreshed page does not contain
  // it -- see the load effect. Not an error: the row exists, this page just
  // cannot show it.
  const [postCreateNote, setPostCreateNote] = useState("");

  // ---- Fetching ------------------------------------------------------------
  //
  // THE QUERY GOES TO THE SERVER; brand, category and origin stay client-side.
  // The endpoint searches part_number/description (both indexed) via ?q= and
  // has no filter for the other three -- sending it anything else would be
  // inventing a parameter. Mirrors the picker exactly, including the debounce
  // and the last-response-wins token; the two screens read one endpoint and
  // should fail and recover the same way.
  //
  // ⚠️ LAST-RESPONSE-WINS, guarded by a token rather than by cancellation.
  // Two searches in flight can land out of order, and without this the slower
  // earlier one overwrites the newer rows -- the list would show results for a
  // query the box no longer contains. A stale answer is dropped whole, error
  // included: reporting a failure for a query nobody is waiting on any more is
  // its own kind of wrong.
  const fetchToken = useRef(0);
  useEffect(() => {
    const token = ++fetchToken.current;
    const query = search.trim();
    setCatalogStatus("loading");

    const timer = setTimeout(async () => {
      const result = await searchProducts(query, CATALOG_LIMIT);
      if (fetchToken.current !== token) return;

      if (!result.ok || !result.products) {
        // CLEARED, not left standing. Stale rows under an error message read
        // as current results and would be quoted from.
        setProducts([]);
        setCatalogError(result.error ?? "The product catalog could not be loaded.");
        setCatalogStatus("error");
        return;
      }
      setProducts(result.products);
      setSearchedFor(query);
      setCatalogError("");
      setCatalogStatus("ok");

      // A just-created product, now back from the server. Selected by ID --
      // the search was set to its part number, and part numbers are NOT
      // unique, so matching on one could open a DIFFERENT product.
      //
      // ⚠️ THE ABSENT CASE IS SAID OUT LOUD. If the id is not on this page
      // (clipped by the limit, or hidden by a filter still set from before),
      // selecting nothing silently would read as a create that did not happen.
      // Neither branch ever selects a different row.
      if (pendingSelectIdRef.current !== null) {
        const wanted = pendingSelectIdRef.current;
        const row = result.products.find((p) => p.id === wanted);
        if (row) {
          setSelected(row);
          setPostCreateNote("");
        } else {
          setPostCreateNote(
            `Created (id ${wanted}) — refine the search to view it.`,
          );
        }
        pendingSelectIdRef.current = null;
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [search, retryTick]);

  // A row that vanished from the results should not stay open in the panel
  // describing a product no longer on screen.
  useEffect(() => {
    setSelected((cur) => (cur && products.some((p) => p.id === cur.id) ? cur : null));
  }, [products]);

  // ---- The derived filter option sets --------------------------------------
  //
  // ⚠️ DERIVED FROM THE LOADED ROWS, NOT RESTATED IN THIS FILE. A hardcoded
  // list goes stale in the silent direction: the brand list here omitted
  // "Poly" for exactly as long as Poly was the only real product in the
  // catalog, so the one row that existed could not be filtered to. Deriving
  // closes that gap by construction -- a brand already in ProductBrand needs
  // no second list edit to become filterable.
  //
  // ⚠️ IT DOES NOT MAKE A *NEW* ODOO BRAND FILTERABLE, and this is not the
  // place that could. toCatalogProduct narrows an unrecognised brand name to
  // null, so it never reaches `p.brand` at all: such a row shows the dash in
  // the Brand column and sits outside every brand option. Adding a sixth
  // brand is still a deploy -- see ProductBrand in types/index.ts, where that
  // decision and its endgame live. Deriving here fixes the list going stale
  // against brands this build ALREADY knows, nothing more.
  //
  // The other honest cost, stated rather than hidden: these describe the
  // LOADED PAGE, not the whole catalog, so a brand present only past the
  // limit is not offered. That is the same page the count line describes and
  // the same page the filters act on, so the control never offers an option
  // that filters to nothing.
  const brandOptions = useMemo(() => {
    const found = new Set<string>();
    for (const p of products) if (p.brand) found.add(p.brand);
    return Array.from(found).sort();
  }, [products]);

  const originOptions = useMemo(() => {
    const found = new Set<string>();
    for (const p of products) if (p.defaultOrigin) found.add(p.defaultOrigin);
    return Array.from(found).sort();
  }, [products]);

  // A select whose only option is "All" filters nothing and invites a click
  // that does nothing, so it is not rendered at all. Its state is reset with
  // it -- otherwise a brand chosen before a search could keep filtering from
  // behind a control that is no longer on screen.
  useEffect(() => {
    if (brandOptions.length === 0) setBrand("All");
  }, [brandOptions]);
  useEffect(() => {
    if (originOptions.length === 0) setOrigin("All");
  }, [originOptions]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (brand !== "All" && p.brand !== brand) return false;
      if (category !== "All" && p.category !== category) return false;
      if (origin !== "All" && p.defaultOrigin !== origin) return false;
      // ⚠️ THE TEXT QUERY IS NOT RE-APPLIED HERE. The endpoint already matched
      // it, against indexed columns, before these rows arrived. Filtering on
      // it again would silently hide rows the server matched on a field or a
      // casing this list does not reproduce.
      return true;
    });
  }, [products, brand, category, origin]);

  const clearFilters = () => {
    setSearch(""); setBrand("All"); setCategory("All"); setOrigin("All");
  };

  const filtersActive = search.length > 0 || brand !== "All" || category !== "All" || origin !== "All";

  // ---- Why the table is empty ----------------------------------------------
  //
  // ⚠️ AN EMPTY CATALOG MUST NEVER READ AS A BROKEN CATALOG, and on the real
  // master this is the ORDINARY case: it holds a handful of products, so most
  // searches legitimately match nothing. When a filter is the cause it is
  // named, because "No products match" sends someone looking for a catalog
  // problem that does not exist.
  const emptyMessage = useMemo(() => {
    if (products.length === 0) {
      return searchedFor
        ? `No product matches “${searchedFor}”.`
        : "The product catalog is empty.";
    }
    const culprits = [
      brand !== "All" ? `Brand: ${brand}` : null,
      category !== "All" ? `Category: ${category}` : null,
      origin !== "All" ? `Origin: ${origin}` : null,
    ].filter(Boolean) as string[];

    if (culprits.length === 0) return "No matching products.";
    const list = culprits.length === 1
      ? culprits[0]
      : `${culprits.slice(0, -1).join(", ")} and ${culprits[culprits.length - 1]}`;
    return `${products.length} ${products.length === 1 ? "product" : "products"} found, `
         + `but ${list} ${culprits.length === 1 ? "hides" : "hide"} `
         + `${products.length === 1 ? "it" : "them all"}.`;
  }, [products.length, searchedFor, brand, category, origin]);

  // A product that now EXISTS on the server. Point the search at it and ask
  // the load effect to select it when it comes back -- see pendingSelectIdRef.
  const handleCreated = (p: CatalogProduct) => {
    setAddOpen(false);
    setPostCreateNote("");
    pendingSelectIdRef.current = p.id;
    setSearch(p.partNo);
  };

  // Products that now EXIST on the server, in bulk (slice import-b).
  //
  // ⚠️ NOTHING IS INSERTED LOCALLY, on exactly the terms handleCreated
  // follows: letting the rows come back from the server is what PROVES they
  // persisted. Nor is any row SELECTED -- an import creates up to 200 products
  // and there is no single one to open.
  //
  // The search is cleared as well as the fetch re-fired, because the rows just
  // imported almost certainly do not match whatever query was in the box. Both
  // are needed: clearing an ALREADY-empty box is an Object.is-equal update that
  // React bails out of, so the effect would never re-run and the new products
  // would not appear.
  const handleImported = (count: number) => {
    setImportOpen(false);
    setSelected(null);
    setPostCreateNote(
      `Imported ${count} ${count === 1 ? "product" : "products"}.`,
    );
    setSearch("");
    setRetryTick((n) => n + 1);
  };

  const tableHeight = typeof window !== "undefined"
    ? Math.max(layout.minTableHeight, window.innerHeight - layout.tableHeightOffset)
    : 480;

  return (
    <div
      data-testid="catalog-page"
      className="flex"
      style={{ height: "calc(100vh - 48px)" }}
    >
      <div className="flex flex-col" style={{ padding: spacing[24], flex: 1, minWidth: 0 }}>
        {/* HEADER */}
        <div className="flex items-start justify-between" style={{ marginBottom: spacing[16] }}>
          <div>
            <div
              data-testid="catalog-title"
              style={{
                fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                letterSpacing: typography.letterSpacing.tight,
              }}
            >
              Catalog
            </div>
            {/* The old subtitle counted the rows and then asserted "4 brands",
                which was a fact about the mock generator rather than about the
                catalog. The count belongs to the count line below; this says
                where the rows come from. */}
            <div
              data-testid="catalog-subtitle"
              style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}
            >
              The product master, read from Odoo.
            </div>
          </div>
          <div className="flex items-center" style={{ gap: spacing[8] }}>
            {/* The two write paths from this screen into the product master:
                one product typed in (1d-b), or up to 200 from a CSV
                (import-b). Both create real records through the same
                allowlist; only the quantity differs. */}
            <button
              data-testid="catalog-import-csv"
              className="btn-secondary"
              onClick={() => setImportOpen(true)}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                fontSize: typography.fontSize.base, cursor: "pointer",
              }}
            >
              Import CSV
            </button>
            <button
              data-testid="catalog-add-product"
              className="btn-primary"
              onClick={() => setAddOpen(true)}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                border: "none", borderRadius: radius.base,
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
              }}
            >
              Add Product
            </button>
          </div>
        </div>

        {/* FILTER BAR */}
        <div
          data-testid="catalog-filter-bar"
          className="flex items-center"
          style={{
            height: heights.filterBar, padding: `0 ${spacing[12]}px`, marginBottom: spacing[12],
            backgroundColor: semanticColors.panelBg,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            gap: spacing[12],
          }}
        >
          <div
            className="flex items-center"
            style={{
              flex: 1, minWidth: layout.searchMinWidthWide,
              height: heights.input, padding: `0 ${spacing[8]}px`,
              backgroundColor: semanticColors.surface,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              gap: spacing[6],
            }}
          >
            <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
            <input
              data-testid="catalog-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search part number or description"
              style={{
                flex: 1, minWidth: 0, height: "100%",
                fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                background: "transparent", border: "none", outline: "none",
              }}
            />
          </div>
          {brandOptions.length > 0 && (
            <select
              data-testid="catalog-filter-brand"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              style={SELECT_STYLE}
            >
              <option value="All">Brand: All</option>
              {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
          <select
            data-testid="catalog-filter-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={SELECT_STYLE}
          >
            {ALL_CATEGORIES.map((c) => <option key={c} value={c}>{c === "All" ? "Category: All" : c}</option>)}
          </select>
          {originOptions.length > 0 && (
            <select
              data-testid="catalog-filter-origin"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              style={SELECT_STYLE}
            >
              <option value="All">Origin: All</option>
              {originOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          <button
            data-testid="catalog-clear-filters"
            onClick={clearFilters}
            style={{
              background: "transparent", border: "none",
              color: filtersActive ? colors.indigo600 : semanticColors.textPlaceholder,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
              cursor: filtersActive ? "pointer" : "default",
              padding: `0 ${spacing[4]}px`,
            }}
          >
            Clear
          </button>
        </div>

        {/* COUNT. Singular matters here: the real catalog is small, so
            "1 of 1 product" is a sentence this actually renders. */}
        <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[4] }}>
          <span data-testid="catalog-filtered-count" className="tabular-nums">
            {filtered.length}
          </span>
          {filtered.length !== products.length ? (
            <> of <span className="tabular-nums">{products.length}</span>{products.length === 1 ? " product" : " products"}</>
          ) : (
            <>{filtered.length === 1 ? " product" : " products"}</>
          )}
          {/* ⚠️ SAID ONLY WHEN THE LIMIT ACTUALLY BIT. A page clipped at 200
              that presents itself as the whole catalog is how someone
              concludes a part does not exist. */}
          {products.length === CATALOG_LIMIT && (
            <span data-testid="catalog-truncated">
              {" "}— first {CATALOG_LIMIT}, refine the search
            </span>
          )}
        </div>

        {/* ⚠️ THE CATALOG CARRIES NO PRICE, AND THAT IS PERMANENT. Every price
            in this system is computed by the engine from the six inputs on a
            quote line; a catalog price would be a second place a number could
            come from, and the first time the two disagreed nobody would know
            which was right. The List Price column is gone rather than dashed
            -- a column that is structurally empty on every row forever is
            noise -- so the fact is stated here instead. */}
        <div
          data-testid="catalog-price-note"
          style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPlaceholder, marginBottom: spacing[8] }}
        >
          Prices are computed per quote line — the catalog carries none.
        </div>

        {/* Non-blocking, and NOT an error: the product was created and the
            server confirmed it: this page simply is not showing it, because
            the id came back outside the current page or filter. Saying nothing
            would read as a create that did not happen. */}
        {postCreateNote !== "" && (
          <div
            data-testid="catalog-postcreate-note"
            style={{
              marginBottom: spacing[8], padding: `${spacing[6]}px ${spacing[10]}px`,
              backgroundColor: semanticColors.accentBg,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
            }}
          >
            {postCreateNote}
          </div>
        )}

        {/* TABLE — THREE STATES, and the error one is NOT an empty table: an
            empty grid under a filter bar reads as "nothing matched", which is
            a statement about the catalog rather than about a request that
            failed. */}
        <div style={{ flex: 1, minHeight: 0 }}>
          {catalogStatus === "loading" ? (
            <div
              data-testid="catalog-loading"
              style={{
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              Loading catalog…
            </div>
          ) : catalogStatus === "error" ? (
            <div
              data-testid="catalog-error"
              className="flex items-start"
              style={{
                gap: spacing[8], padding: `${spacing[10]}px ${spacing[12]}px`,
                backgroundColor: semanticColors.destructiveBg,
                border: `1px solid ${semanticColors.destructiveBorder}`,
                borderRadius: radius.base,
                fontSize: typography.fontSize.sm, color: semanticColors.destructive,
              }}
            >
              <AlertTriangle
                size={iconSize.md}
                strokeWidth={iconStroke.regular}
                style={{ flexShrink: 0, marginTop: spacing[2] }}
              />
              <span>
                <strong>The product catalog could not be loaded.</strong>{" "}
                <span data-testid="catalog-error-detail">{catalogError}</span>{" "}
                {/* No mock fallback and no "showing cached results" -- there
                    are none. Retrying is the only honest offer. */}
                <button
                  data-testid="catalog-retry"
                  onClick={() => setRetryTick((n) => n + 1)}
                  style={{
                    background: "none", border: "none", padding: 0,
                    color: semanticColors.destructive, textDecoration: "underline",
                    cursor: "pointer", font: "inherit",
                  }}
                >
                  Retry
                </button>
              </span>
            </div>
          ) : (
            <CatalogTable
              rows={filtered}
              height={tableHeight}
              onRowClick={setSelected}
              selectedId={selected?.id}
              emptyMessage={emptyMessage}
            />
          )}
        </div>
      </div>

      {selected && (
        <ProductDetailPanel
          product={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {/* position: fixed, so where either sits in the tree does not matter. */}
      {addOpen && (
        <NewProductModal
          onClose={() => setAddOpen(false)}
          onCreated={handleCreated}
        />
      )}

      {importOpen && (
        <ImportCsvModal
          onClose={() => setImportOpen(false)}
          onImported={handleImported}
        />
      )}
    </div>
  );
}
